import React, { useEffect, useState } from 'react';
import PipelineShell from '../common/PipelineShell';
import FileTree from '../common/FileTree';
import useStageJob from '../common/useStageJob';
import AgentActivityGraph from '../common/AgentActivityGraph';
import {
  getProject,
  setStageData,
  patchStage,
  subscribe,
  buildStageContext,
} from '../../../services/pipelineProject.service';
import { runPipelineStage } from '../../../services/pipeline.service';
import '../Pipeline.css';

/**
 * Tests Stage
 *
 * Generates unit, integration and e2e test suites for the generated code.
 */
function TestsStage() {
  const [project, setProject]   = useState(getProject());
  const [testsData, setTestsData] = useState(null);
  const [error, setError]       = useState(null);
  const [activity, setActivity] = useState([]);

  useEffect(() => {
    const p = getProject();
    if (p.stages?.tests?.data) setTestsData(p.stages.tests.data);
    return subscribe(setProject);
  }, []);

  const handleResult = (result) => {
    setTestsData(result || {});
    setStageData('tests', result || {});
    setError(null);
  };

  const handleActivity = (acts) => setActivity(acts || []);
  const { polling, error: pollError, startPolling } = useStageJob(handleResult, setError, handleActivity);

  const handleGenerate = async () => {
    setError(null);
    const context = buildStageContext('tests');
    const comment = project?.stages?.tests?.comment || '';
    try {
      const res = await runPipelineStage('tests', context, comment);
      if (res.status === 'accepted') {
        patchStage('tests', { jobId: res.jobId });
        startPolling(res.jobId);
      } else if (res.status === 'completed') {
        handleResult(res.result);
        if (res.agentActivity) setActivity(res.agentActivity);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to generate tests.');
    }
  };

  const code         = project?.stages?.code?.data;
  const hasTests     = Boolean(testsData?.files?.length);
  const displayError = error || pollError;

  // Group files by type
  const groupedFiles = {};
  (testsData?.files || []).forEach((f) => {
    const type = f.type || 'other';
    if (!groupedFiles[type]) groupedFiles[type] = [];
    groupedFiles[type].push(f);
  });
  const typeLabels = { unit: '🔬 Unit', integration: '🔗 Integration', e2e: '🌐 E2E', other: '📋 Other' };

  return (
    <PipelineShell
      stageId="tests"
      description="Generate comprehensive test suites covering unit, integration and end-to-end scenarios."
      canApprove={hasTests}
      headerAction={
        <button
          className="pipeline-btn primary"
          onClick={handleGenerate}
          disabled={polling || !code}
          title={!code ? 'Complete the Code stage first' : undefined}
        >
          {polling ? '⏳ Generating…' : hasTests ? '↩ Regenerate' : '🧪 Generate Tests'}
        </button>
      }
    >
      {!code && (
        <div className="pipeline-empty">
          <div className="pipeline-empty-icon">🔒</div>
          <p>Complete and approve the <strong>Code</strong> stage first.</p>
        </div>
      )}

      {(polling || activity.length > 0) && (
        <AgentActivityGraph activity={activity} polling={polling} />
      )}

      {polling && (
        <div className="pipeline-loading">
          <div className="pipeline-spinner" />
          <p>Generating test suites…</p>
        </div>
      )}

      {displayError && !polling && <div className="pipeline-error">{displayError}</div>}

      {hasTests && !polling && (
        <>
          {/* Summary */}
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
            <span className="pipeline-file-count-badge">{testsData.files.length} test files</span>
            {Object.entries(groupedFiles).map(([type, files]) => (
              <span key={type} className="pipeline-tag gray">
                {typeLabels[type] || type}: {files.length}
              </span>
            ))}
            {testsData.coverage && (
              <span className="pipeline-tag green">~{testsData.coverage} coverage</span>
            )}
          </div>

          {testsData.testingStrategy && (
            <div className="pipeline-card">
              <div className="pipeline-card-title">Testing Strategy</div>
              <p style={{ margin: 0, fontSize: '0.88rem', color: '#475569' }}>{testsData.testingStrategy}</p>
            </div>
          )}

          <FileTree files={testsData.files} />
        </>
      )}
    </PipelineShell>
  );
}

export default TestsStage;
